import { logger } from '@/utils/logger';

import { StateCreator } from 'zustand';
import {
    Auth,
    User,
    onAuthStateChanged as firebaseOnAuthStateChanged,
    signInWithPopup,
    GoogleAuthProvider,
    signOut,
    signInWithEmailAndPassword,
    createUserWithEmailAndPassword,
    sendPasswordResetEmail,
    signInWithCredential
} from 'firebase/auth';

/** Minimal interface for E2E mock auth objects that provide their own listener */
interface E2EMockAuth {
    onAuthStateChanged: (callback: (user: User | null) => void) => () => void;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    signInAnonymously: () => Promise<any>;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    signInWithEmailAndPassword: (email: string, pass: string) => Promise<any>;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    createUserWithEmailAndPassword: (email: string, pass: string) => Promise<any>;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    sendPasswordResetEmail: (email: string) => Promise<any>;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    signInWithPopup: (provider: any) => Promise<any>;
    signOut: () => Promise<void>;
}

/**
 * Enhanced auth listener with E2E mock support.
 * Ensures that if a mock auth object provides its own listener, we use it.
 */
const onAuthStateChanged = (authObj: Auth | E2EMockAuth, callback: (user: User | null) => void) => {
    if (typeof (authObj as E2EMockAuth).onAuthStateChanged === 'function') {
        return (authObj as E2EMockAuth).onAuthStateChanged(callback);
    }
    return firebaseOnAuthStateChanged(authObj as Auth, callback);
};

const wrappedSignInAnonymously = (authObj: Auth | E2EMockAuth) => {
    if (typeof (authObj as E2EMockAuth).signInAnonymously === 'function') {
        return (authObj as E2EMockAuth).signInAnonymously();
    }
    throw new Error('Guest login is disabled outside the Firebase E2E mock.');
};

const wrappedSignInWithEmailAndPassword = (authObj: Auth | E2EMockAuth, email: string, pass: string) => {
    if (typeof (authObj as E2EMockAuth).signInWithEmailAndPassword === 'function') {
        return (authObj as E2EMockAuth).signInWithEmailAndPassword(email, pass);
    }
    return signInWithEmailAndPassword(authObj as Auth, email, pass);
};

const wrappedCreateUserWithEmailAndPassword = (authObj: Auth | E2EMockAuth, email: string, pass: string) => {
    if (typeof (authObj as E2EMockAuth).createUserWithEmailAndPassword === 'function') {
        return (authObj as E2EMockAuth).createUserWithEmailAndPassword(email, pass);
    }
    return createUserWithEmailAndPassword(authObj as Auth, email, pass);
};

const wrappedSendPasswordResetEmail = (authObj: Auth | E2EMockAuth, email: string) => {
    if (typeof (authObj as E2EMockAuth).sendPasswordResetEmail === 'function') {
        return (authObj as E2EMockAuth).sendPasswordResetEmail(email);
    }
    return sendPasswordResetEmail(authObj as Auth, email);
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const wrappedSignInWithPopup = (authObj: Auth | E2EMockAuth, provider: any) => {
    if (typeof (authObj as E2EMockAuth).signInWithPopup === 'function') {
        return (authObj as E2EMockAuth).signInWithPopup(provider);
    }
    return signInWithPopup(authObj as Auth, provider);
};

const wrappedSignOut = (authObj: Auth | E2EMockAuth) => {
    if (typeof (authObj as E2EMockAuth).signOut === 'function') {
        return (authObj as E2EMockAuth).signOut();
    }
    return signOut(authObj as Auth);
};
import { auth, db } from '@/services/firebase';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { isFirebaseE2EMockEnabled } from '@/utils/e2eMode';
import { isAnonymousOrDemoUser } from '@/utils/authGuards';

/** Type guard for Firebase Auth errors */
interface FirebaseAuthError {
    code: string;
    message: string;
}

/**
 * Maps a Firebase Auth error code to a user-friendly message.
 * Centralised to avoid duplicating error strings across every auth method.
 */
function getAuthErrorMessage(error: FirebaseAuthError): string | null {
    // Firebase may embed the full blocked origin in the error code, for example:
    // auth/requests-from-referer-http://localhost:4243-are-blocked
    if (
        typeof error.code === 'string' &&
        error.code.startsWith('auth/requests-from-referer-') &&
        error.code.endsWith('-are-blocked')
    ) {
        return 'Authentication service not configured for this domain. Please contact support.';
    }

    switch (error.code) {
        // ── Config / service issues ────────────────────────────────────────
        case 'auth/argument-error':
        case 'auth/invalid-api-key':
            return 'Authentication service unavailable. Please try again later.';

        // ── Popup dismissed (not a real error — user cancelled) ────────────
        case 'auth/popup-closed-by-user':
        case 'auth/cancelled-popup-request':
            return null; // Suppress — the user intentionally dismissed the dialog

        // ── Credential errors ──────────────────────────────────────────────
        case 'auth/user-not-found':
        case 'auth/wrong-password':
        case 'auth/invalid-credential':
            return 'Incorrect email or password. Please try again.';

        // ── Sign-up specific ───────────────────────────────────────────────
        case 'auth/email-already-in-use':
            return 'An account with this email already exists. Try signing in instead.';
        case 'auth/weak-password':
            return 'Password must be at least 6 characters.';
        case 'auth/invalid-email':
            return 'Please enter a valid email address.';

        // ── Rate limiting ──────────────────────────────────────────────────
        case 'auth/too-many-requests':
            return 'Too many sign-in attempts. Please wait a few minutes and try again.';

        // ── Network ────────────────────────────────────────────────────────
        case 'auth/network-request-failed':
            return 'Network error. Check your internet connection and try again.';

        // ── Account disabled ───────────────────────────────────────────────
        case 'auth/user-disabled':
            return 'This account has been disabled. Contact support for help.';

        // ── Admin-restricted (sign-in provider not enabled) ────────────────
        case 'auth/admin-restricted-operation':
        case 'auth/operation-not-allowed':
            return 'This sign-in method is not enabled. Contact administrator.';

        // ── Re-authentication required (stale session for destructive ops) ─
        case 'auth/requires-recent-login':
            return 'Your session has expired. Please sign in again before making this change.';

        // ── Domain authorization (Firebase Console configuration) ──────────
        case 'auth/requests-from-referer-blocked':
        case 'auth/requests-from-referer-empty-are-blocked':
            return 'Authentication service not configured for this domain. Please contact support.';

        // ── Fallback ───────────────────────────────────────────────────────
        default:
            return error.message ?? 'Authentication failed';
    }
}

/** INDII DESIGN SYSTEM - Single Source of Truth state */
// Define the shape of our Auth state
export interface AuthSlice {
    user: User | null;
    authLoading: boolean;
    authError: string | null;
    isSignUpMode: boolean;
    passwordResetSent: boolean;

    // Actions
    loginWithGoogle: () => Promise<void>;
    loginWithEmail: (email: string, pass: string) => Promise<void>;
    signUpWithEmail: (email: string, pass: string) => Promise<void>;
    loginAsGuest: () => Promise<void>;
    logout: () => Promise<void>;
    resetPassword: (email: string) => Promise<void>;
    setSignUpMode: (isSignUp: boolean) => void;
    clearAuthError: () => void;
    initializeAuthListener: () => () => void;
}

// Create the slice
export const createAuthSlice: StateCreator<AuthSlice> = (set, get) => ({
    user: null,
    authLoading: true,
    authError: null,
    isSignUpMode: false,
    passwordResetSent: false,

    loginWithGoogle: async () => {
        try {
            set({ authLoading: true, authError: null });
            const provider = new GoogleAuthProvider();
            await wrappedSignInWithPopup(auth, provider);
            // State update handled by listener
        } catch (error: unknown) {
            const firebaseError = error as FirebaseAuthError;
            const errorMessage = getAuthErrorMessage(firebaseError);
            // If errorMessage is null (popup dismissed), reset loading but don't show an error
            if (errorMessage === null) {
                set({ authLoading: false });
            } else {
                set({ authError: errorMessage, authLoading: false });
            }
        }
    },

    loginWithEmail: async (email: string, pass: string) => {
        try {
            set({ authLoading: true, authError: null });
            await wrappedSignInWithEmailAndPassword(auth, email, pass);
            // State update handled by listener
        } catch (error: unknown) {
            const firebaseError = error as FirebaseAuthError;
            const errorMessage = getAuthErrorMessage(firebaseError);
            set({ authError: errorMessage ?? 'Sign-in failed', authLoading: false });
            throw error;
        }
    },

    signUpWithEmail: async (email: string, pass: string) => {
        try {
            set({ authLoading: true, authError: null });
            const userCred = await wrappedCreateUserWithEmailAndPassword(auth, email, pass);

            // Create user document in Firestore immediately (don't wait for listener)
            if (userCred.user) {
                const userDocRef = doc(db, 'users', userCred.user.uid);
                await setDoc(userDocRef, {
                    uid: userCred.user.uid,
                    email: userCred.user.email,
                    displayName: '',
                    createdAt: serverTimestamp(),
                    updatedAt: serverTimestamp(),
                }, { merge: true });
            }

            set({ authLoading: false });
            // State update handled by listener — onAuthStateChanged will update currentUser
        } catch (error: unknown) {
            const firebaseError = error as FirebaseAuthError;
            const errorMessage = getAuthErrorMessage(firebaseError);
            set({ authError: errorMessage ?? 'Account creation failed', authLoading: false });
            throw error;
        }
    },

    loginAsGuest: async () => {
        try {
            set({ authLoading: true, authError: null });

            if (!isFirebaseE2EMockEnabled()) {
                throw new Error('Guest login is disabled. Sign in or create an account to continue.');
            }

            await wrappedSignInAnonymously(auth);
            if (typeof window !== 'undefined') {
                window.localStorage.setItem('cookie-consent', '{"analytics":false,"marketing":false}');
            }
            // State update handled by listener normally
        } catch (error: unknown) {
            const firebaseError = error as FirebaseAuthError;
            const errorMessage = getAuthErrorMessage(firebaseError) ?? 'Guest login failed';
            set({ authError: errorMessage, authLoading: false });
            throw error;
        }
    },

    resetPassword: async (email: string) => {
        try {
            set({ authLoading: true, authError: null, passwordResetSent: false });
            await wrappedSendPasswordResetEmail(auth, email);
            set({ authLoading: false, passwordResetSent: true });
        } catch (error: unknown) {
            const firebaseError = error as FirebaseAuthError;
            let errorMessage: string;

            switch (firebaseError.code) {
                case 'auth/user-not-found':
                    // Security: Don't reveal whether the email exists.
                    // Still show "success" to prevent enumeration attacks.
                    set({ authLoading: false, passwordResetSent: true });
                    return;
                case 'auth/invalid-email':
                    errorMessage = 'Please enter a valid email address.';
                    break;
                case 'auth/too-many-requests':
                    errorMessage = 'Too many requests. Please wait a few minutes before trying again.';
                    break;
                default:
                    errorMessage = getAuthErrorMessage(firebaseError) ?? 'Failed to send reset email. Please try again.';
            }
            set({ authError: errorMessage, authLoading: false });
        }
    },

    setSignUpMode: (isSignUp: boolean) => {
        set({ isSignUpMode: isSignUp, authError: null, passwordResetSent: false });
    },

    clearAuthError: () => {
        set({ authError: null });
    },

    logout: async () => {
        try {
            await wrappedSignOut(auth);

            // ST9: Clear cache on logout to prevent stale data cross-contamination
            const { cacheService } = await import('@/services/cache/CacheService');
            cacheService.clear();

            // Clear all active Firestore subscriptions to prevent permission errors
            import('@/core/store').then(({ useStore }) => {
                const state = useStore.getState() as unknown as Record<string, unknown>;
                if (typeof state.clearAllSubscriptions === 'function') {
                    (state.clearAllSubscriptions as () => void)();
                }
            }).catch(err => logger.error('[Auth] Failed to clear subscriptions during logout:', err));

            set({ user: null, authError: null, isSignUpMode: false, passwordResetSent: false });
        } catch (error: unknown) {
            const message = error instanceof Error ? error.message : 'Logout failed';
            set({ authError: message });
        }
    },

    initializeAuthListener: () => {
        logger.debug('[Auth] Initializing Auth Listener...');

        // 6. Electron Auth Handoff Listener (Item 518)
        let electronUnsub: (() => void) | null = null;
        const electronAuth = typeof window !== 'undefined' ? window.electronAPI?.auth : null;
        if (electronAuth && electronAuth.onUserUpdate) {
            electronUnsub = electronAuth.onUserUpdate(async (tokens) => {
                if (tokens) {
                    if (tokens.source === 'founder') {
                        try {
                            localStorage.setItem('indii_founder_preview_pending', 'true');
                        } catch {
                            // localStorage may be unavailable; the app will still sign in.
                        }
                    }
                    logger.info('[Auth] Received handoff tokens from Main Process. Signing in...');
                    try {
                        set({ authLoading: true, authError: null });
                        const credential = GoogleAuthProvider.credential(tokens.idToken, tokens.accessToken || undefined);
                        await signInWithCredential(auth, credential);
                        // onAuthStateChanged will handle the rest
                    } catch (err) {
                        logger.error('[Auth] Failed to sign in with handoff tokens:', err);
                        set({ authError: 'Authentication handoff failed. Please try again.', authLoading: false });
                    }
                } else {
                    logger.info('[Auth] Received logout signal from Main Process.');
                    // User already signed out in main, just clear local state if listener doesn't fire
                    set({ user: null, authLoading: false });
                }
            });
        }

        // 7. Electron Auth Error Listener
        let errorUnsub: (() => void) | null = null;
        if (electronAuth && electronAuth.onError) {
            errorUnsub = electronAuth.onError((data: { message: string }) => {
                logger.error('[Auth] Received auth error from Main Process:', data.message);
                set({ authError: data.message, authLoading: false });
            });
        }

        // FAST FAIL: If no API key, don't wait for Firebase (it might hang or crash)
        // EXCLUSION: If we are using the E2E mock, allow any API key string.
        const apiKey = auth.app?.options?.apiKey;
        const apiKeyLower = apiKey?.toLowerCase() ?? '';
        const isE2EMock = isFirebaseE2EMockEnabled();

        if (!isE2EMock && (!apiKey || apiKeyLower.includes('fake') || apiKeyLower.includes('bypass') || apiKeyLower.includes('mock') || apiKeyLower.includes('your_'))) {
            logger.warn('[Auth] No valid API Key found and not in E2E mode.');
            set({ authLoading: false });
            return () => {
                if (electronUnsub) electronUnsub();
                if (errorUnsub) errorUnsub();
            };
        }

        // TIMEOUT FAILSAFE: If onAuthStateChanged never fires (API key blocked,
        // network down, SDK error), fall through to login after 10s instead of
        // spinning forever. See: API_KEY_SERVICE_BLOCKED incident 2026-03-11.
        let hasResolved = false;
        const timeoutId = setTimeout(() => {
            if (!hasResolved) {
                logger.error('[Auth] Auth listener timed out after 10s — falling through to login.');
                set({
                    authLoading: false,
                    authError: 'Authentication timed out. The service may be temporarily unavailable. Please try again.',
                });
            }
        }, 10_000);

        // BUG-002 FIX: Debounce rapid null→user transitions during token refresh.
        // Firebase may briefly emit null between token refresh cycles,
        // causing spurious logouts under rapid load (100+ clicks/sec).
        let debounceTimer: ReturnType<typeof setTimeout> | null = null;
        let lastKnownUser: typeof auth.currentUser = null;

        // Return unsubscribe function
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            logger.info(`[Auth] onAuthStateChanged fired. User: ${user ? user.uid : 'null'}, lastKnownUser: ${lastKnownUser ? lastKnownUser.uid : 'null'}`);
            if (isE2EMock) {
                logger.debug('[Auth] Mock Auth Listener Fired', { userUid: user?.uid });
            }
            hasResolved = true;
            clearTimeout(timeoutId);

            // Clear any lingering timeout error once auth state resolves
            if (get().authError?.includes('timed out')) {
                set({ authError: null });
            }

            // If transitioning FROM a valid user TO null, debounce it.
            // This prevents the brief null flash during Firebase Auth token refresh.
            if (!user && lastKnownUser) {
                logger.info('[Auth] User went null — debouncing for 500ms to guard against token refresh race...');
                if (debounceTimer) clearTimeout(debounceTimer);
                debounceTimer = setTimeout(() => {
                    // Re-check: if auth.currentUser is STILL null after 500ms,
                    // it's a genuine logout — not a token refresh blip.
                    if (!auth.currentUser) {
                        logger.info('[Auth] Confirmed logout after debounce.');
                        lastKnownUser = null;
                        set({ user: null, authLoading: false, authError: null });
                    } else {
                        logger.info('[Auth] Token refresh resolved — user is still authenticated.');
                        lastKnownUser = auth.currentUser;
                        set({ user: auth.currentUser, authLoading: false, authError: null });
                    }
                }, 500);
                return;
            }

            // A null event arriving WHILE the login debounce is pending (i.e.
            // right after the login event, before its 500ms timer resolved)
            // used to fall through to the steady-state branch below, which
            // cancelled the pending login and logged the user out even though
            // Firebase is still signed in — the token-refresh null blip that
            // follows a login is indistinguishable at fire time from a logout,
            // so the pending timer (which re-checks auth.currentUser) is the
            // only authority. Ignore the blip; the login timer decides.
            if (!user && debounceTimer) {
                logger.info('[Auth] Null during login debounce — ignoring transient null.');
                return;
            }

            // BUG-002 FIX: Debounce rapid null→user transitions during token refresh.
            // Firebase may briefly emit null between token refresh cycles,
            // causing spurious logouts under rapid load (100+ clicks/sec).
            if (user && !lastKnownUser) {
                logger.info('[Auth] User went from null to valid — debouncing for 500ms to guard against rapid transitions...');
                if (debounceTimer) clearTimeout(debounceTimer);
                debounceTimer = setTimeout(async () => {
                    const currentUser = auth.currentUser || user;
                    if (currentUser) {
                        logger.info('[Auth] Confirmed login after debounce. Setting authLoading to false.');
                        lastKnownUser = currentUser;
                        set({ user: currentUser, authLoading: false, authError: null });

                        if (isFirebaseE2EMockEnabled() || isAnonymousOrDemoUser(currentUser)) {
                            logger.warn('[Auth] Mock/Anonymous user detected — skipping Firestore user sync.');
                            return;
                        }

                        // Optional: Ensure user document exists in Firestore
                        try {
                            const userRef = doc(db, 'users', currentUser.uid);
                            const userSnap = await getDoc(userRef);

                            if (!userSnap.exists()) {
                                await setDoc(userRef, {
                                    uid: currentUser.uid,
                                    email: currentUser.email,
                                    displayName: currentUser.displayName,
                                    photoURL: currentUser.photoURL,
                                    createdAt: serverTimestamp(),
                                    lastLogin: serverTimestamp()
                                }, { merge: true });
                            } else {
                                // Update last login
                                await setDoc(userRef, {
                                    lastLogin: serverTimestamp()
                                }, { merge: true });
                            }
                        } catch (e: unknown) {
                            logger.error("[Auth] Failed to sync user to Firestore", e);
                        }
                    } else {
                        logger.info('[Auth] Debounce finished but currentUser is null. Setting authLoading to false.');
                        set({ authLoading: false, authError: null });
                    }
                }, 500);
                return;
            }

            // Clear any pending debounce if we received a steady state
            if (debounceTimer) {
                clearTimeout(debounceTimer);
                debounceTimer = null;
            }

            logger.info(`[Auth] Steady state reached. Setting authLoading to false. User: ${user ? user.uid : 'null'}`);
            lastKnownUser = user;
            set({ user, authLoading: false, authError: null });

            if (user && !isFirebaseE2EMockEnabled() && !isAnonymousOrDemoUser(user)) {
                // Optional: Ensure user document exists in Firestore
                try {
                    const userRef = doc(db, 'users', user.uid);
                    const userSnap = await getDoc(userRef);

                    if (!userSnap.exists()) {
                        await setDoc(userRef, {
                            uid: user.uid,
                            email: user.email,
                            displayName: user.displayName,
                            photoURL: user.photoURL,
                            createdAt: serverTimestamp(),
                            lastLogin: serverTimestamp()
                        }, { merge: true });
                    } else {
                        // Update last login
                        await setDoc(userRef, {
                            lastLogin: serverTimestamp()
                        }, { merge: true });
                    }
                } catch (e: unknown) {
                    logger.error("[Auth] Failed to sync user to Firestore", e);
                }
            } else if (user) {
                logger.warn('[Auth] Anonymous/demo user detected — skipping Firestore user sync.');
            }
        });

        return () => {
            clearTimeout(timeoutId);
            if (debounceTimer) clearTimeout(debounceTimer);
            if (electronUnsub) electronUnsub();
            if (errorUnsub) errorUnsub();
            unsubscribe();
        };
    }
});
